"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../../contexts/AuthContext"
import Swal from "sweetalert2"
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CurrencyRupeeIcon,
  UserIcon,
  ArrowLeftIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline"

export default function RefundHistoryPage() {
  const { user, fetchCampaignHistory, refundNow, cancelRefund, fetchAllUsers } = useAuth()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [history, setHistory] = useState([])
  const [summary, setSummary] = useState({})
  const [users, setUsers] = useState([])
  const [selectedUserId, setSelectedUserId] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(25)
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0 })
  const [refundingId, setRefundingId] = useState(null)
  const [cancellingId, setCancellingId] = useState(null)
  const [bulkRefunding, setBulkRefunding] = useState(false)

  // Live timer tick to update countdowns smoothly
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60000)
    return () => clearInterval(interval)
  }, [])

  // Load all users on mount
  useEffect(() => {
    loadUsersList()
  }, [])

  // Load history whenever filters change
  useEffect(() => {
    loadRefundLogs()
  }, [selectedUserId, statusFilter, page, limit, tick])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1)
      loadRefundLogs()
    }, 400)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const loadUsersList = async () => {
    try {
      if (fetchAllUsers) {
        const res = await fetchAllUsers()
        if (Array.isArray(res)) {
          // Filter ONLY users who have failedRefundEnabled: true
          const refundEnabledUsers = res.filter((u) => u.settings?.billing?.failedRefundEnabled === true)
          setUsers(refundEnabledUsers)
        }
      }
    } catch (e) {
      console.error("Error loading users:", e)
    }
  }

  const loadRefundLogs = async () => {
    try {
      setLoading(true)
      const filters = {}
      if (statusFilter && statusFilter !== "all") {
        filters.status = statusFilter
      }
      if (searchTerm.trim()) {
        filters.search = searchTerm.trim()
      }

      const result = await fetchCampaignHistory(selectedUserId || null, filters, page, limit)
      if (result.success) {
        setHistory(result.history || [])
        setSummary(result.summary || {})
        setPagination(result.pagination || { currentPage: page, totalPages: 1, totalRecords: 0 })
      }
    } catch (error) {
      console.error("Error loading refund logs:", error)
    } finally {
      setLoading(false)
    }
  }

  const formatCredits = (value) => {
    const num = Number(value || 0)
    if (Number.isNaN(num)) return value || "0"
    return num.toFixed(2)
  }

  const formatDate = (date) => (date ? new Date(date).toLocaleString("en-IN") : "-")

  const calculateCountdown = (scheduledAt, isRefunded, status) => {
    if (isRefunded) {
      return { text: "Refunded Success", color: "text-green-700 bg-green-50 border-green-200" }
    }
    if (status === "cancelled") {
      return { text: "Refund Cancelled", color: "text-gray-600 bg-gray-50 border-gray-200" }
    }
    if (!scheduledAt) {
      return { text: "Unscheduled", color: "text-gray-600 bg-gray-50 border-gray-200" }
    }

    const diff = new Date(scheduledAt).getTime() - Date.now()
    if (diff <= 0) {
      return { text: "⚡ Ready to Refund", color: "text-blue-700 bg-blue-50 border-blue-200 animate-pulse" }
    }

    const hours = Math.floor(diff / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    return {
      text: `⏳ ${hours}h ${minutes}m left`,
      color: "text-amber-800 bg-amber-50 border-amber-200",
    }
  }

  const handleRefundCampaign = async (record) => {
    const result = await Swal.fire({
      title: "Direct Instant Refund",
      html: `
        <div class="text-left text-sm space-y-2">
          <p><strong>Campaign:</strong> ${record.campaignName}</p>
          <p><strong>Template:</strong> ${record.templateName || "Unknown"}</p>
          <p><strong>User:</strong> ${record.userName} (${record.userEmail || ""})</p>
          <p><strong>Failed Messages:</strong> ${record.failedMessages || 0}</p>
          <p><strong>Refund Amount:</strong> <span class="text-green-600 font-bold">₹${formatCredits(record.refundAmount)}</span></p>
          <p class="text-xs text-gray-500 mt-2">This will immediately credit the user's balance and mark this campaign as Refund Success.</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Refund Now!",
    })

    if (!result.isConfirmed) return

    try {
      setRefundingId(record.id)
      const res = await refundNow({ campaignId: record.id || record.campaignId })
      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Refund Successful!",
          text: res.message || `Successfully refunded ₹${formatCredits(record.refundAmount)}`,
          timer: 2000,
          showConfirmButton: false,
        })
        loadRefundLogs()
      } else {
        Swal.fire({
          icon: "error",
          title: "Refund Failed",
          text: res.error || "Failed to process refund",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setRefundingId(null)
    }
  }

  const handleCancelCampaignRefund = async (record) => {
    const result = await Swal.fire({
      title: "Cancel Campaign Refund?",
      html: `
        <div class="text-left text-sm space-y-2">
          <p><strong>Campaign:</strong> ${record.campaignName}</p>
          <p><strong>User:</strong> ${record.userName || "User"}</p>
          <p><strong>Failed Messages:</strong> ${record.failedMessages || 0}</p>
          <p class="text-xs text-red-500 mt-2">This will cancel the automatic refund for failed messages in this campaign.</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Cancel Refund",
    })

    if (!result.isConfirmed) return

    try {
      setCancellingId(record.id)
      const res = await cancelRefund({ campaignId: record.id || record.campaignId })
      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Refund Cancelled",
          text: res.message || "Refund cancelled successfully",
          timer: 2000,
          showConfirmButton: false,
        })
        loadRefundLogs()
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: res.error || "Failed to cancel refund",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setCancellingId(null)
    }
  }

  const handleRefundAllEligible = async () => {
    const eligibleCount = (summary?.totalReady || 0) + (summary?.totalScheduled || 0)
    if (eligibleCount === 0) {
      Swal.fire({
        icon: "info",
        title: "No Eligible Messages",
        text: "There are currently no failed messages waiting for refund.",
        confirmButtonColor: "#4A7C59",
      })
      return
    }

    const targetUserObj = users.find((u) => String(u._id || u.id) === String(selectedUserId))
    const userLabel = targetUserObj ? `${targetUserObj.name || targetUserObj.username}` : "All Users"

    const result = await Swal.fire({
      title: "Bulk Instant Refund",
      html: `
        <div class="text-left text-sm space-y-2">
          <p><strong>Target:</strong> ${userLabel}</p>
          <p><strong>Failed Messages:</strong> ${eligibleCount}</p>
          <p><strong>Estimated Total:</strong> <span class="text-green-600 font-bold">₹${formatCredits(summary?.scheduledAmount || 0)}</span></p>
          <p class="text-xs text-red-500 mt-2">Are you sure you want to immediately execute refunds for all eligible messages?</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Execute Bulk Refund!",
    })

    if (!result.isConfirmed) return

    try {
      setBulkRefunding(true)
      const payload = selectedUserId ? { userId: selectedUserId } : {}
      const res = await refundNow(payload)
      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Bulk Refund Completed!",
          text: res.message,
          confirmButtonColor: "#4A7C59",
        })
        loadRefundLogs()
      } else {
        Swal.fire({
          icon: "error",
          title: "Bulk Refund Error",
          text: res.error || "Failed to execute bulk refund",
          confirmButtonColor: "#4A7C59",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
        confirmButtonColor: "#4A7C59",
      })
    } finally {
      setBulkRefunding(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50/50 dark:bg-gray-900 p-4 lg:p-8">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/meta")}
              className="p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-100 transition-colors shadow-sm"
              title="Back to Meta Pricing"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            </button>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <CurrencyRupeeIcon className="w-8 h-8 text-green-600" />
              Failed Credit Refund Management
            </h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 ml-11">
            Real-time tracking of failed message refunds, 24-hour schedules, live countdowns, and 1-click instant balance refunds.
          </p>
        </div>

        {/* Top Action Buttons */}
        <div className="flex items-center gap-3 ml-11 md:ml-0">
          <button
            onClick={loadRefundLogs}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition shadow-sm"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin text-green-600" : ""}`} />
            Refresh
          </button>
          <button
            onClick={handleRefundAllEligible}
            disabled={bulkRefunding || ((summary?.totalReady || 0) + (summary?.totalScheduled || 0) === 0)}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl text-sm font-bold shadow-md shadow-green-600/20 disabled:opacity-50 transition transform active:scale-95"
          >
            <ShieldCheckIcon className="w-5 h-5" />
            {bulkRefunding ? "Processing..." : `Refund All Eligible (${(summary?.totalReady || 0) + (summary?.totalScheduled || 0)})`}
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Card 1: Total Failed Messages */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Total Failed</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                {(summary?.totalFailed || 0).toLocaleString()}
              </h3>
              <p className="text-xs text-gray-500 mt-1">Total Charged: ₹{formatCredits(summary?.totalCharged || 0)}</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center text-red-600">
              <XCircleIcon className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Card 2: Scheduled (24h Delay) */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-amber-200 dark:border-amber-900/50 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">Scheduled (24h Delay)</p>
              <h3 className="text-2xl font-bold text-amber-900 dark:text-amber-200 mt-1">
                {(summary?.totalScheduled || 0).toLocaleString()}
              </h3>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">Auto-refund in progress</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center text-amber-600">
              <ClockIcon className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Card 3: Ready to Refund */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-blue-200 dark:border-blue-900/50 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-400">Ready To Refund</p>
              <h3 className="text-2xl font-bold text-blue-900 dark:text-blue-200 mt-1">
                {(summary?.totalReady || 0).toLocaleString()}
              </h3>
              <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">24h delay reached / ready</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-600">
              <ArrowPathIcon className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Card 4: Refund Success */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-green-200 dark:border-green-900/50 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-green-700 dark:text-green-400">Refund Success</p>
              <h3 className="text-2xl font-bold text-green-900 dark:text-green-200 mt-1">
                {(summary?.refundCount || 0).toLocaleString()}
              </h3>
              <p className="text-xs text-green-700 dark:text-green-400 mt-1">Credited: ₹{formatCredits(summary?.totalRefunded || 0)}</p>
            </div>
            <div className="w-12 h-12 rounded-2xl bg-green-50 dark:bg-green-900/30 flex items-center justify-center text-green-600">
              <CheckCircleIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4 mb-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* User selector */}
          <div className="flex items-center gap-3">
            <UserIcon className="w-5 h-5 text-gray-500" />
            <select
              value={selectedUserId}
              onChange={(e) => {
                setSelectedUserId(e.target.value)
                setPage(1)
              }}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-medium focus:ring-2 focus:ring-green-500 min-w-[220px]"
            >
              <option value="">All Users</option>
              {users.map((u) => (
                <option key={u._id || u.id} value={u._id || u.id}>
                  {u.name || u.username} ({u.email})
                </option>
              ))}
            </select>
          </div>

          {/* Status Tabs */}
          <div className="flex flex-wrap items-center gap-2">
            {[
              { id: "all", label: "All Logs" },
              { id: "scheduled", label: "Scheduled (24h)" },
              { id: "ready", label: "Ready to Refund" },
              { id: "refunded", label: "Refund Success" },
              { id: "cancelled", label: "Cancelled" },
              { id: "failed", label: "Failed" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setStatusFilter(tab.id)
                  setPage(1)
                }}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                  statusFilter === tab.id
                    ? "bg-green-600 text-white shadow-sm"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative min-w-[240px]">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search campaign, template, error..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>
      </div>

      {/* Main Refund Logs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 dark:bg-gray-700/60 border-b border-gray-200 dark:border-gray-700 text-xs font-semibold uppercase text-gray-600 dark:text-gray-300 tracking-wider">
              <tr>
                <th className="py-3.5 px-4 whitespace-nowrap">Date & Time</th>
                <th className="py-3.5 px-4">User</th>
                <th className="py-3.5 px-4">Campaign</th>
                <th className="py-3.5 px-4 text-center">Total / Failed</th>
                <th className="py-3.5 px-4">Status & Countdown</th>
                <th className="py-3.5 px-4">Failure Reason</th>
                <th className="py-3.5 px-4 text-right">Deducted</th>
                <th className="py-3.5 px-4 text-right">Balance Before</th>
                <th className="py-3.5 px-4 text-right">Refund Amount</th>
                <th className="py-3.5 px-4 text-right">Balance After</th>
                <th className="py-3.5 px-4 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={11} className="py-12 text-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm text-gray-500">Loading refund logs...</span>
                    </div>
                  </td>
                </tr>
              ) : history.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-12 text-center text-gray-500">
                    <ExclamationCircleIcon className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                    <p className="font-semibold text-gray-700 dark:text-gray-300">No logs found</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your user or status filter.</p>
                  </td>
                </tr>
              ) : (
                history.map((record) => {
                  const countdown = calculateCountdown(record.refundScheduledAt, record.status === "refunded", record.status)
                  const isRefunded = record.status === "refunded"

                  return (
                    <tr
                      key={record.id}
                      className="hover:bg-gray-50/80 dark:hover:bg-gray-700/40 transition-colors"
                    >
                      {/* Date & Time */}
                      <td className="py-3.5 px-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400">
                        {formatDate(record.createdAt)}
                      </td>

                      {/* User */}
                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-gray-900 dark:text-white">{record.userName}</div>
                        <div className="text-xs text-gray-500">{record.userEmail}</div>
                      </td>

                      {/* Campaign & Template */}
                      <td className="py-3.5 px-4">
                        <div className="font-medium text-gray-900 dark:text-white truncate max-w-[160px]" title={record.campaignName}>
                          {record.campaignName}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">{record.templateName}</div>
                      </td>

                      {/* Total / Failed */}
                      <td className="py-3.5 px-4 text-center">
                        <div className="font-semibold text-gray-900 dark:text-white text-xs">
                          {(record.totalMessages || 0).toLocaleString()}
                        </div>
                        <div className="text-[11px] font-bold text-red-500">
                          {(record.failedMessages || 0).toLocaleString()} failed
                        </div>
                      </td>

                      {/* Status & Live Countdown */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${countdown.color}`}
                        >
                          {countdown.text}
                        </span>
                        {record.refundScheduledAt && !isRefunded && record.status !== "cancelled" && (
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            Target: {formatDate(record.refundScheduledAt)}
                          </div>
                        )}
                        {record.refundedAt && isRefunded && (
                          <div className="text-[10px] text-green-600 mt-0.5">
                            Refunded: {formatDate(record.refundedAt)}
                          </div>
                        )}
                      </td>

                      {/* Failure Reason */}
                      <td className="py-3.5 px-4 max-w-[200px]">
                        <span
                          className="text-xs font-medium text-red-600 dark:text-red-400 block truncate"
                          title={record.failureReason}
                        >
                          {record.failureReason || "Delivery Failed"}
                        </span>
                      </td>

                      {/* Deducted Amount */}
                      <td className="py-3.5 px-4 text-right font-medium text-gray-900 dark:text-white">
                        ₹{formatCredits(record.chargeAmount)}
                      </td>

                      {/* Balance Before */}
                      <td className="py-3.5 px-4 text-right text-xs text-gray-600 dark:text-gray-300 font-mono">
                        {record.balanceBefore !== null && record.balanceBefore !== undefined
                          ? `₹${formatCredits(record.balanceBefore)}`
                          : "-"}
                      </td>

                      {/* Refund Amount */}
                      <td className="py-3.5 px-4 text-right font-bold text-green-600 dark:text-green-400 font-mono">
                        ₹{formatCredits(record.refundAmount)}
                      </td>

                      {/* Balance After */}
                      <td className="py-3.5 px-4 text-right text-xs text-gray-600 dark:text-gray-300 font-mono">
                        {record.balanceAfter !== null && record.balanceAfter !== undefined
                          ? `₹${formatCredits(record.balanceAfter)}`
                          : "-"}
                      </td>

                      {/* Direct Action: Refund Now & Cancel */}
                      <td className="py-3.5 px-4 text-center whitespace-nowrap">
                        {isRefunded ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2.5 py-1 rounded-lg border border-green-200">
                            <CheckCircleIcon className="w-4 h-4" />
                            Refunded Success
                          </span>
                        ) : record.status === "cancelled" ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 bg-gray-100 px-2.5 py-1 rounded-lg border border-gray-200">
                            <XCircleIcon className="w-4 h-4" />
                            Refund Cancelled
                          </span>
                        ) : (
                          <div className="flex items-center justify-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleRefundCampaign(record)}
                              disabled={refundingId === record.id || cancellingId === record.id}
                              className="inline-flex items-center gap-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg shadow transition transform active:scale-95 disabled:opacity-50"
                            >
                              <CurrencyRupeeIcon className="w-3.5 h-3.5" />
                              {refundingId === record.id ? "Refunding..." : "Refund Now"}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCancelCampaignRefund(record)}
                              disabled={refundingId === record.id || cancellingId === record.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-bold rounded-lg transition active:scale-95 disabled:opacity-50"
                              title="Cancel refund for this campaign"
                            >
                              <XCircleIcon className="w-3.5 h-3.5" />
                              {cancellingId === record.id ? "Cancelling..." : "Cancel"}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 gap-3">
          <div className="text-xs text-gray-500">
            Page {pagination.currentPage || page} of {pagination.totalPages || 1}
            {pagination.totalRecords ? ` (${pagination.totalRecords.toLocaleString()} total records)` : ""}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={(pagination.currentPage || page) <= 1}
              className="px-3 py-1.5 text-xs font-semibold bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 disabled:opacity-40 hover:bg-gray-50"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pagination.totalPages || 1, p + 1))}
              disabled={(pagination.currentPage || page) >= (pagination.totalPages || 1)}
              className="px-3 py-1.5 text-xs font-semibold bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
